'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { 
  Smartphone, Shield, Zap, ArrowRight, Users, Target, Trophy, Gift, 
  CheckCircle, Sparkles, Star, Heart, Github, Twitter, Mail, Globe,
  ChevronRight, Play, BarChart3, Award, Clock
} from 'lucide-react';

const features = [
  { icon: Users, title: 'User Management', desc: 'Complete user lifecycle & profiles', gradient: 'from-blue-500 to-indigo-600' },
  { icon: Target, title: 'Smart Challenges', desc: 'AI-powered screen-free activities', gradient: 'from-orange-500 to-red-600' },
  { icon: Trophy, title: 'Competitions', desc: 'Engaging leaderboards & rewards', gradient: 'from-amber-500 to-yellow-600' },
  { icon: Gift, title: 'Store & Rewards', desc: 'Points redemption system', gradient: 'from-purple-500 to-fuchsia-600' },
  { icon: BarChart3, title: 'Analytics', desc: 'Real-time usage insights', gradient: 'from-emerald-500 to-teal-600' },
  { icon: Award, title: 'Achievements', desc: 'Gamified milestone system', gradient: 'from-pink-500 to-rose-600' },
];

const apiModules = [
  { name: 'Authentication & Users', color: 'bg-blue-500' },
  { name: 'Questionnaires & Onboarding', color: 'bg-cyan-500' },
  { name: 'Dashboard & Analytics', color: 'bg-emerald-500' },
  { name: 'Screen Time Tracking', color: 'bg-amber-500' },
  { name: 'Challenges & Proofs', color: 'bg-orange-500' },
  { name: 'XP & Points System', color: 'bg-purple-500' },
  { name: 'Friends & Safety Net', color: 'bg-pink-500' },
  { name: 'Competitions', color: 'bg-yellow-500' },
  { name: 'Monthly Goals', color: 'bg-teal-500' },
  { name: 'Store & Redemptions', color: 'bg-fuchsia-500' },
  { name: 'Notifications', color: 'bg-red-500' },
  { name: 'Admin Panel APIs', color: 'bg-indigo-500' },
];

const stats = [
  { value: '70+', label: 'API Endpoints' },
  { value: '22', label: 'Database Tables' },
  { value: '12', label: 'Core Modules' },
  { value: '100%', label: 'TypeScript' },
];

export default function Home() {
  return (
    <div className="min-h-dvh w-full overflow-x-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-indigo-950 text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 right-0 w-[300px] h-[300px] bg-pink-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <motion.div 
                whileHover={{ rotate: 10, scale: 1.1 }}
                className="w-11 h-11 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30"
              >
                <Smartphone size={22} className="text-white" />
              </motion.div>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold sm:text-xl">ScreenSavvy</h1>
                <p className="text-xs text-slate-400">Backend API</p>
              </div>
            </div>
            <nav
              className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-300 md:gap-6"
              aria-label="Primary"
            >
              <a href="#features" className="transition-colors hover:text-white">
                Features
              </a>
              <a href="#api" className="transition-colors hover:text-white">
                API
              </a>
              <a href="#tech" className="transition-colors hover:text-white">
                Tech Stack
              </a>
            </nav>
            <motion.div
              className="w-full sm:w-auto sm:shrink-0"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                href="/admin/login"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-center text-sm font-semibold text-slate-900 shadow-lg shadow-white/10 transition-all hover:shadow-xl hover:shadow-white/20 sm:w-auto"
              >
                <Shield size={16} />
                Admin Login
              </Link>
            </motion.div>
          </div>
        </motion.header>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-full mb-6"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-sm font-medium text-indigo-200">API Server Running</span>
              <Sparkles size={14} className="text-amber-400" />
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              Build Healthier
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                Screen Habits
              </span>
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10"
            >
              Complete backend infrastructure for ScreenSavvy mobile app. 
              Built with Next.js 16, Prisma ORM, and MySQL for scalable performance.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/admin/login"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl font-semibold shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 transition-all"
                >
                  Access Admin Panel
                  <ArrowRight size={18} />
                </Link>
              </motion.div>
              <motion.a
                href="#api"
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-all"
              >
                <Play size={18} />
                View API Docs
              </motion.a>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              >
                <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Features */}
        <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 bg-indigo-500/20 text-indigo-300 rounded-full text-sm font-medium mb-4">
              Features
            </span>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">Everything You Need</h3>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive backend features to power your screen-time management app
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon size={22} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{feature.title}</h4>
                  <p className="text-slate-400 text-sm">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* API Modules */}
        <section id="api" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden"
          >
            <div className="p-6 md:p-8 border-b border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Zap size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">API Modules</h3>
                    <p className="text-sm text-slate-400">All available endpoints</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium">
                  <CheckCircle size={14} /> All Systems Operational
                </span>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {apiModules.map((module, index) => (
                  <motion.div
                    key={module.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    <div className={`w-2 h-2 ${module.color} rounded-full`} />
                    <span className="text-sm text-slate-300">{module.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Tech Stack */}
        <section id="tech" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium mb-4">
              Tech Stack
            </span>
            <h3 className="text-3xl md:text-4xl font-bold mb-8">Built With Modern Tools</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['Next.js 16', 'TypeScript', 'Prisma ORM', 'MySQL', 'JWT Auth', 'Tailwind CSS', 'Framer Motion'].map((tech, i) => (
                <motion.span
                  key={tech}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="px-5 py-2.5 bg-gradient-to-r from-white/10 to-white/5 border border-white/20 rounded-full text-sm font-medium"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-8 md:p-12 text-center"
          >
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
            <div className="relative">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-indigo-100 max-w-xl mx-auto mb-8">
                Access the admin panel to start managing your ScreenSavvy application
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/admin/login"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all"
                >
                  Access Admin Panel
                  <ChevronRight size={18} />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="md:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Smartphone size={18} className="text-white" />
                  </div>
                  <span className="font-bold">ScreenSavvy</span>
                </div>
                <p className="text-sm text-slate-400 mb-4">
                  Building healthier screen time habits through engaging challenges and rewards.
                </p>
                <div className="flex items-center gap-3">
                  <a href="#" className="p-2 bg-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/20 transition-all">
                    <Twitter size={16} />
                  </a>
                  <a href="#" className="p-2 bg-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/20 transition-all">
                    <Github size={16} />
                  </a>
                  <a href="#" className="p-2 bg-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/20 transition-all">
                    <Globe size={16} />
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Product</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#api" className="hover:text-white transition-colors">API</a></li>
                  <li><a href="#tech" className="hover:text-white transition-colors">Tech Stack</a></li>
                  <li><Link href="/admin/login" className="hover:text-white transition-colors">Admin Panel</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Resources</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Changelog</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Legal</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Cookie Policy</a></li>
                </ul>
              </div>
            </div>
            
            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
              <p>© {new Date().getFullYear()} ScreenSavvy. All rights reserved.</p>
              <p>
                Developed By Mtoag
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
